export {
  clawsChatStream,
  clawsCreateSession,
  clawsDeleteSession,
  clawsGetChatHistory,
  clawsGetMyClaws as getMyClaws,
  clawsGetReplaySession,
  clawsListQuickPrompts,
  clawsListSessions,
  clawsSearchMessages,
  clawsShareSession,
  clawsSubscribeClawSessionsState,
  clawsUpdateSession,
  clawsUpdateSessionReadCursor,
} from '@/app/services/claw';

import type {
  ClawsListCasesData,
  ClawsListFolderData,
  ClawsListMySkillsData,
  ClawV1ChatRequest,
  ClawV1Content,
  ClawV1CreateFolderRequest,
  ClawV1DeleteRequest,
  ClawV1GetUploadUrlRequest,
} from '@/app/services/claw';
import {
  clawsChat,
  clawsCreateFolder,
  clawsCreateShare,
  clawsDelete,
  clawsGetClawChannelConfig,
  clawsGetDownloadUrl,
  clawsGetSessionCoordinator,
  clawsGetUploadUrl,
  clawsListCases,
  clawsListFolder,
  clawsListInstalledSkills,
  clawsListMySkills,
  clawsModerateUpload,
  clawsPollChannelBindFlow,
  clawsSearchMessages,
  clawsSetClawChannelConfig,
  clawsStartChannelBindFlow,
  clawsSubmitMessageTasks,
  clawsUpdateMessage,
} from '@/app/services/claw';
import { feedbacksCreate } from '@/app/services/tools';
import type { ClawBillingMode } from '@/app/types';

export const listCases = (query?: ClawsListCasesData['query']) => clawsListCases({ query });

// 平台已发布的 skill 目录（DB），供输入框 `/` 唤起的技能选择器展示。query 支持 keyword 搜索与分页。
export const listMySkills = (query?: ClawsListMySkillsData['query']) => clawsListMySkills({ query });

// 指定 claw 实例上实际安装的运行时 skill（直连 picoclaw）。picoclaw 不可达时返回 reachable=false。
export const listInstalledSkills = (clawId: string) => clawsListInstalledSkills({ path: { claw_id: clawId } });

export const createFolder = (body: ClawV1CreateFolderRequest) => clawsCreateFolder({ body });

export const listFolder = (query: ClawsListFolderData['query']) => clawsListFolder({ query });

export const getUploadUrl = (body: ClawV1GetUploadUrlRequest) => clawsGetUploadUrl({ body });

export const moderateUpload = (body: { claw_id: string; file_path: string }) => clawsModerateUpload({ body });

export const deleteFileOrFolder = (body: ClawV1DeleteRequest) => clawsDelete({ body });

export const getDownloadUrl = clawsGetDownloadUrl;

export { clawsPollWeixinBindFlow, clawsStartWeixinBindFlow } from '@/app/services/claw';

export const getClawChannelConfig = (clawId: string, channel: string) =>
  clawsGetClawChannelConfig({ path: { channel }, query: { claw_id: clawId } });

export const setClawChannelConfig = (
  channel: string,
  body: {
    claw_id: string;
    config: Record<string, unknown>;
  },
) => clawsSetClawChannelConfig({ path: { channel }, body });

export const startClawChannelBindFlow = (body: {
  claw_id: string;
  provider: 'CHANNEL_BIND_PROVIDER_WEIXIN' | 'CHANNEL_BIND_PROVIDER_WECOM';
}) => clawsStartChannelBindFlow({ body });

export const pollClawChannelBindFlow = (flowId: string) => clawsPollChannelBindFlow({ path: { flow_id: flowId } });

export const createShare = clawsCreateShare;

export const getSessionCoordinator = (sessionId: string) =>
  clawsGetSessionCoordinator({ query: { session_id: sessionId } });

type SendChatPayload = ClawV1ChatRequest & {
  session_id: string;
  msg: { role: 'user'; contents: ClawV1Content[] };
  billing_mode?: ClawBillingMode;
};

export const sendChat = (payload: SendChatPayload) => clawsChat({ body: payload });

export const searchMessages = (query: string, pageToken?: string) =>
  clawsSearchMessages({
    query: {
      query,
      'pager.pagesz': 20,
      ...(pageToken ? { 'pager.page_token': pageToken } : { 'pager.page': 0 }),
    },
  });

// 试用挽留弹窗的反馈走通用 feedbacks 接口，统一用 'claw' 作为来源标识
export const submitClawExpireRetainFeedback = (content: string) =>
  feedbacksCreate({
    body: {
      obj_type: 'agent_container',
      type: 'other',
      content: `claw_pass_trial_expire: ${content}`,
    },
  });

export const updateMessage = (messageId: string, content: ClawV1Content[]) =>
  clawsUpdateMessage({ path: { message_id: messageId }, body: { message_id: messageId, content } });

export const submitMessageTasks = (messageId: string) =>
  clawsSubmitMessageTasks({ path: { message_id: messageId }, body: { message_id: messageId } });
